import {
  HmacAppliedPlacement,
  HmacAppliedProduct,
  MoneyValue,
  PlacementFormatElement,
  Product,
  ProductList,
  ProductVariant,
} from '../../generated/graphql'
import { productPrice } from '../product/price'

export type Beacon = {
  url: string
  hmac: string
}

export type HmacAppliedProductWithBeacon = Omit<
  HmacAppliedProduct,
  | 'onBasketChangeBeacon'
  | 'onClickBeacon'
  | 'onLoadBeacon'
  | 'onViewBeacon'
  | 'onWishlistBeacon'
  | 'product'
> & {
  onBasketChangeBeacon?: Beacon | null
  onClickBeacon?: Beacon | null
  onLoadBeacon?: Beacon | null
  onViewBeacon?: Beacon | null
  onWishlistBeacon?: Beacon | null
  product: Product & {
    price: {
      price: MoneyValue | null
      rrp: MoneyValue | null
    }
  }
  productVariant?: ProductVariant & {
    price: {
      price: MoneyValue | null
      rrp: MoneyValue | null
    }
  }
}

export type HmacAppliedProductWithWidgetProduct = HmacAppliedProduct & {
  widgetProductItem: {
    product: Product
    productVariant: ProductVariant
  }
}

export type ProductListWithSponsoredAds = Omit<
  ProductListWithBeacon,
  'facets' | 'hasMore' | 'total' | 'products'
> & {
  sponsoredAds?: HmacAppliedPlacementWithBeacon[] | null
}

export type PlacementFormatElementWithBeacon = Omit<
  PlacementFormatElement,
  'value'
> & {
  value?: HmacAppliedProductWithBeacon[] | null
}

export type HmacAppliedPlacementWithBeacon = Omit<
  HmacAppliedPlacement,
  | 'onClickBeacon'
  | 'onLoadBeacon'
  | 'onViewBeacon'
  | 'placementFormatToProducts'
> & {
  onClickBeacon?: Beacon | null
  onLoadBeacon?: Beacon | null
  onViewBeacon?: Beacon | null
  placementFormatToProducts: PlacementFormatElementWithBeacon[] | null
}

export type ProductListWithBeacon = Omit<ProductList, 'sponsoredAds'> & {
  sponsoredAds?: HmacAppliedPlacementWithBeacon[] | null
}

// will remove as soon as Horizon adds pagination to the SponsoredAds query
const NUMBER_OF_SPONSORED_ADS = 3

export const extractSponsoredProductData = (
  productListWithSponsoredAds: HmacAppliedProductWithWidgetProduct[],
) => {
  const hasNullProducts = productListWithSponsoredAds?.some((sponsoredAd) => {
    return sponsoredAd?.widgetProductItem?.product === null
  })

  const filteredProducts = productListWithSponsoredAds?.filter(
    (sponsoredAd, index) =>
      ((!hasNullProducts && sponsoredAd?.widgetProductItem?.product) ||
        sponsoredAd?.widgetProductItem?.productVariant) &&
      index < NUMBER_OF_SPONSORED_ADS,
  )

  if (!filteredProducts) {
    return []
  }

  const products = filteredProducts.map((sponsoredAd) => {
    const transformedPrice = productPrice(
      sponsoredAd?.widgetProductItem?.product,
    )

    const variantRRP =
      sponsoredAd?.widgetProductItem?.productVariant?.price?.rrp
    const variantPrice =
      sponsoredAd?.widgetProductItem?.productVariant?.price?.price

    const variantImages =
      sponsoredAd?.widgetProductItem?.productVariant?.images.length > 0
        ? sponsoredAd?.widgetProductItem?.productVariant?.images
        : sponsoredAd?.widgetProductItem?.productVariant?.product?.images

    return {
      __typename: sponsoredAd.__typename,
      onLoadBeacon: sponsoredAd?.onLoadBeacon
        ? JSON.parse(atob(sponsoredAd?.onLoadBeacon))
        : null,
      onViewBeacon: sponsoredAd?.onViewBeacon
        ? JSON.parse(atob(sponsoredAd?.onViewBeacon))
        : null,
      onWishlistBeacon: sponsoredAd?.onWishlistBeacon
        ? JSON.parse(atob(sponsoredAd?.onWishlistBeacon))
        : null,
      onBasketChangeBeacon: sponsoredAd?.onBasketChangeBeacon
        ? JSON.parse(atob(sponsoredAd?.onBasketChangeBeacon))
        : null,
      onClickBeacon: sponsoredAd?.onClickBeacon
        ? JSON.parse(atob(sponsoredAd?.onClickBeacon))
        : null,
      product: !hasNullProducts
        ? {
            ...sponsoredAd?.widgetProductItem?.product,
            price: transformedPrice && {
              price: {
                defaultPrice: transformedPrice.price,
                __typename: 'MoneyValue',
              },
              rrp: {
                defaultPrice: transformedPrice.rrp,
                __typename: 'MoneyValue',
              },
              __typename: 'ProductPrice',
            },
            inStock:
              sponsoredAd?.widgetProductItem?.product?.cheapestVariant?.inStock,
            marketedSpecialOffer: sponsoredAd?.widgetProductItem?.product
              ?.marketedSpecialOffer && {
              ...sponsoredAd?.widgetProductItem?.product?.marketedSpecialOffer,
              descriptionText:
                sponsoredAd?.widgetProductItem?.product?.marketedSpecialOffer
                  ?.description?.content[0]?.content,
              titleText:
                sponsoredAd?.widgetProductItem?.product?.marketedSpecialOffer
                  ?.title?.content[0]?.content,
            },
          }
        : {
            ...sponsoredAd?.widgetProductItem?.productVariant,
            images: variantImages,
            url: sponsoredAd?.widgetProductItem?.productVariant.product?.url,
            price: {
              price: {
                defaultPrice:
                  sponsoredAd?.widgetProductItem?.productVariant.price?.price
                    ?.displayValue,
                __typename: 'MoneyValue',
              },
              rrp: {
                defaultPrice:
                  variantRRP &&
                  variantPrice?.amount &&
                  parseFloat(variantPrice?.amount) <
                    parseFloat(variantRRP?.amount)
                    ? variantRRP.displayValue
                    : null,
                __typename: 'MoneyValue',
              },
              __typename: 'ProductPrice',
            },
            marketedSpecialOffer: sponsoredAd?.widgetProductItem?.productVariant
              ?.marketedSpecialOffer && {
              ...sponsoredAd?.widgetProductItem?.productVariant
                ?.marketedSpecialOffer,
              descriptionText:
                sponsoredAd?.widgetProductItem?.productVariant
                  ?.marketedSpecialOffer?.description?.content[0]?.content,
              titleText:
                sponsoredAd?.widgetProductItem?.productVariant
                  ?.marketedSpecialOffer?.title?.content[0]?.content,
            },
          },
    }
  })

  return products
}
